import {
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl } from '@angular/forms';

import { MomentModule } from 'ngx-moment';

import { type MemberSelectItem, TeamMemberSelectComponent } from '@core/ask-hr-templates-smart-ui';
import { priorityColorMap } from '@core/ask-hr-templates-util';
import type { AskHrTicket } from '@core/ask-hr-tickets-types';
import { injectStatusTranslations, statusIconMap } from '@core/ask-hr-tickets-util';
import type { AskTicketPriority, AskTicketStatus, TellentMemberBrief } from '@core/ask-hr-types';
import { FnCallPipe, NgTemplateAssertType } from '@core/common-legacy';
import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import type { BackgroundToken } from '@core/theme-util';
import {
  AvatarComponent,
  ButtonComponent,
  ButtonDropdownIndicatorDirective,
  ConnectedOverlayDirective,
  DropdownComponent,
  DropdownItemComponent,
  IconButtonComponent,
  IconComponent,
  type IconName,
  type IconToken,
  InlineOpenStrategy,
  OverlayOriginDirective,
  OverlayPosition,
  provideOpenStrategy,
  RtTooltip,
  Select2Component,
  StrategyRelevance,
} from '@core/user-interface-legacy';
import { getInitials, objectEntries, tokenToCssVar } from '@core/utils-legacy';

@Component({
  selector: 'ask-hr-ticket-info-card',
  templateUrl: 'ticket-info-card.component.html',
  styleUrl: './ticket-info-card.component.less',
  imports: [
    TranslatePipe,
    IconComponent,
    ButtonComponent,
    MomentModule,
    AvatarComponent,
    FnCallPipe,
    ButtonDropdownIndicatorDirective,
    DropdownComponent,
    DropdownItemComponent,
    ConnectedOverlayDirective,
    OverlayOriginDirective,
    TeamMemberSelectComponent,
    NgTemplateAssertType,
    RtTooltip,
    IconButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [provideOpenStrategy(InlineOpenStrategy, StrategyRelevance.Custom)],
})
export class TicketInfoCardComponent {
  protected readonly priorityColorMap = priorityColorMap;
  protected readonly priorityTranslations: Record<AskTicketPriority, string> = {
    low: injectTranslate()('tlc.ask_hr.requests.priority_levels.low'),
    normal: injectTranslate()('tlc.ask_hr.requests.priority_levels.normal'),
    high: injectTranslate()('tlc.ask_hr.requests.priority_levels.high'),
    urgent: injectTranslate()('tlc.ask_hr.requests.priority_levels.urgent'),
  };
  protected readonly priorityDropdownItems = objectEntries(this.priorityTranslations).map(
    ([key, value]) => ({
      key,
      value,
      color: this.priorityColorMap[<AskTicketPriority>key],
    }),
  );
  protected readonly statusColorMap: Record<
    AskTicketStatus,
    Record<'default' | 'hover' | 'pressed', BackgroundToken>
  > = {
    open: {
      default: 'colorBgAppliedDefault',
      hover: 'colorBgAppliedHover',
      pressed: 'colorBgAppliedPressed',
    },
    in_progress: {
      default: 'colorBgInterviewDefault',
      hover: 'colorBgInterviewHover',
      pressed: 'colorBgInterviewPressed',
    },
    closed: {
      default: 'colorBgHiredDefault',
      hover: 'colorBgHiredHover',
      pressed: 'colorBgHiredPressed',
    },
  };
  protected readonly ticketStatuses: AskTicketStatus[] = ['open', 'in_progress', 'closed'];
  protected readonly statusIconColorMap: Record<AskTicketStatus, IconToken> = {
    open: 'colorIconDefault',
    in_progress: 'colorIconInfoDefault',
    closed: 'colorIconSuccessDefault',
  };
  protected readonly statusIconMap: Record<AskTicketStatus, IconName> = statusIconMap;
  protected readonly statusTranslations: Record<AskTicketStatus, string> =
    injectStatusTranslations();
  protected readonly OverlayPosition = OverlayPosition;
  protected readonly MembersSelectType: {
    $implicit: Select2Component<MemberSelectItem>;
    inOverlay: boolean;
  };

  public ticket = input.required<AskHrTicket>();

  public canCancel = input<boolean>(false);
  public canClose = input<boolean>(false);
  public isCancelVisible = input<boolean>(false);
  public canChangeStatus = input<boolean>(false);
  public canChangePriority = input<boolean>(false);
  public canChangeAssignees = input<boolean>(false);

  public priorityChanged = output<AskTicketPriority>();
  public statusChanged = output<AskTicketStatus>();
  public assigneesChanged = output<string[]>();
  public cancelClick = output();

  protected statusDropdownHover = signal(false);
  protected requesterInitials = computed(() =>
    getInitials(this.ticket().requester.firstName, this.ticket().requester.lastName),
  );
  protected assigneesControl = new FormControl<string[]>([], { nonNullable: true });

  private setAssignees = effect(() => {
    this.assigneesControl.setValue(this.ticket().assignees.map(assignee => assignee.guid));
  });

  protected assertMemberSelectItem = (item: any): item is MemberSelectItem => item;
  protected getStatusColor = (status: AskTicketStatus, isHover: boolean, isPressed: boolean) => {
    const colorMap = this.statusColorMap[status];
    if (isPressed) return tokenToCssVar(colorMap.pressed);
    if (isHover) return tokenToCssVar(colorMap.hover);
    return tokenToCssVar(colorMap.default);
  };

  protected onAssigneesChange(assignees?: TellentMemberBrief[]) {
    this.assigneesChanged.emit((assignees ?? []).map(assignee => assignee.guid));
  }
}
